import { createTheme, type Theme } from "@mui/material/styles";
import { m3Roles, type M3Roles } from "./tokens.js";

declare module "@mui/material/styles" {
  interface Palette {
    m3: M3Roles;
  }
  interface PaletteOptions {
    m3?: M3Roles;
  }
}

const FONT = '"Roboto Flex Variable", "Roboto Flex", Roboto, system-ui, -apple-system, "Segoe UI", sans-serif';

/** M3 type scale (https://m3.material.io/styles/typography/type-scale-tokens). */
const type = (size: number, line: number, weight = 400, tracking = 0) => ({
  fontFamily: FONT,
  fontSize: `${size / 16}rem`,
  lineHeight: `${line / 16}rem`,
  fontWeight: weight,
  letterSpacing: `${tracking / size}em`,
});

function palette(mode: "light" | "dark") {
  const r = m3Roles(mode);
  return {
    mode,
    m3: r,
    primary: { main: r.primary, contrastText: r.onPrimary },
    secondary: { main: r.secondary, contrastText: r.onSecondary },
    error: { main: r.error, contrastText: r.onError, light: r.errorContainer, dark: r.onErrorContainer },
    warning: { main: r.warning, contrastText: r.onWarning, light: r.warningContainer, dark: r.onWarningContainer },
    success: { main: r.success, contrastText: r.onSuccess, light: r.successContainer, dark: r.onSuccessContainer },
    info: { main: r.secondary, contrastText: r.onSecondary },
    background: { default: r.surface, paper: r.surfaceContainerLow },
    text: { primary: r.onSurface, secondary: r.onSurfaceVariant },
    divider: r.outlineVariant,
    action: { selected: r.secondaryContainer },
  };
}

export function createDespejoTheme(): Theme {
  return createTheme({
    cssVariables: { colorSchemeSelector: "data" },
    colorSchemes: { light: { palette: palette("light") }, dark: { palette: palette("dark") } },
    shape: { borderRadius: 12 },
    breakpoints: { values: { xs: 0, sm: 600, md: 840, lg: 1240, xl: 1600 } }, // M3 window size classes
    typography: {
      fontFamily: FONT,
      h1: type(36, 44), // display small
      h2: type(32, 40), // headline large
      h3: type(28, 36), // headline medium
      h4: type(24, 32), // headline small
      h5: type(22, 28), // title large
      h6: type(16, 24, 500, 0.15), // title medium
      subtitle1: type(16, 24, 500, 0.15),
      subtitle2: type(14, 20, 500, 0.1),
      body1: type(16, 24, 400, 0.5),
      body2: type(14, 20, 400, 0.25),
      caption: type(12, 16, 400, 0.4),
      button: { ...type(14, 20, 500, 0.1), textTransform: "none" },
      overline: { ...type(11, 16, 500, 0.5), textTransform: "uppercase" },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        // M3 button labels are single-line; wrapping squeezed "Solo esta línea" into three lines.
        styleOverrides: { root: { borderRadius: 20, minHeight: 40, paddingInline: 24, whiteSpace: "nowrap", flexShrink: 0 } },
      },
      // Numbers are right-aligned: never break "₡160 218,52" across lines.
      MuiTableCell: { styleOverrides: { root: { fontVariantNumeric: "tabular-nums" }, alignRight: { whiteSpace: "nowrap" } } },
      MuiIconButton: { styleOverrides: { root: { width: 48, height: 48 } } },
      MuiCard: {
        defaultProps: { variant: "outlined" },
        styleOverrides: { root: ({ theme }) => ({ borderRadius: 12, backgroundColor: theme.vars.palette.m3.surfaceContainerLow }) },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 8, fontWeight: 500 } } },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: { root: ({ theme }) => ({ backgroundColor: theme.vars.palette.m3.surface, color: theme.vars.palette.m3.onSurface }) },
      },
      MuiTextField: { defaultProps: { variant: "outlined", fullWidth: true } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 4 } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 28 } } },
      MuiSnackbarContent: {
        styleOverrides: { root: ({ theme }) => ({ backgroundColor: theme.vars.palette.m3.inverseSurface, color: theme.vars.palette.m3.inverseOnSurface }) },
      },
      MuiTooltip: { defaultProps: { arrow: false } },
      MuiLink: { defaultProps: { underline: "hover" } },
      MuiCssBaseline: {
        styleOverrides: {
          ":focus-visible": { outline: "3px solid currentColor", outlineOffset: 2 },
        },
      },
    },
  });
}
